<mat-card class="detail-card">
    @let params = params$ | async;
    <mat-card-header class="detail-card-header">
        <mat-card-title data-test="title">Fund Valuation Overview</mat-card-title>
    </mat-card-header>
    <mat-card-content class="detail-card-content">
        <div class="table-toolbar">
            <span class="spacer"></span>
            @if (selectMode) {
                <span class="bulk-action-count">{{ selection.selected.length }} selected</span>
                @if (canDelete) {
                    <button mat-stroked-button color="warn" (click)="deleteSelected()">
                        <mat-icon>delete</mat-icon>
                        Delete
                    </button>
                }
                <button mat-button (click)="exitSelectMode()">Clear</button>
            }
            @if (searchVisible) {
                <input matInput #searchInput id="search-input" class="list-search-input" (input)="onSearchInput($event)" placeholder="Search by description or fund">
            }
            <button mat-icon-button (click)="toggleSearch()" matTooltip="Search">
                <mat-icon>search</mat-icon>
            </button>
            <button mat-icon-button (click)="openTableSettings()" matTooltip="Table Settings">
                <mat-icon>view_column</mat-icon>
            </button>
        </div>

        <div class="table-container">
            @if (isLoading$ | async) {
                <div class="loading-overlay">
                    <mat-spinner diameter="40"></mat-spinner>
                </div>
            }

            <table mat-table [dataSource]="(entries$ | async) ?? []" matSort (matSortChange)="onSortChange($event)" [matSortActive]="sortActive" [matSortDirection]="sortDirection">
                <ng-container matColumnDef="select">
                    <th mat-header-cell *matHeaderCellDef></th>
                    <td mat-cell *matCellDef="let entry" (click)="$event.stopPropagation()">
                        <mat-checkbox color="primary"
                                      (change)="selection.toggle(entry)"
                                      [checked]="selection.isSelected(entry)">
                        </mat-checkbox>
                    </td>
                </ng-container>

                <ng-container matColumnDef="fundValuationId">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="id">ID</th>
                    <td mat-cell *matCellDef="let entry">{{entry.fundValuationId}}</td>
                </ng-container>

                <ng-container matColumnDef="description">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="description">Description</th>
                    <td mat-cell *matCellDef="let entry">
                        {{entry.description | traceableFormat}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="fundName">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="fundName">Fund</th>
                    <td mat-cell *matCellDef="let entry">{{entry.fundName | traceableFormat}}</td>
                </ng-container>

                <ng-container matColumnDef="valuationType">
                    <th mat-header-cell *matHeaderCellDef>Valuation Type</th>
                    <td mat-cell *matCellDef="let entry">{{entry.valuationType.code | codeFormat: codeTableValuationType | async}}</td>
                </ng-container>

                <ng-container matColumnDef="reportDate">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="reportDate">Report Date</th>
                    <td mat-cell *matCellDef="let entry">{{entry.reportDate | traceableFormat}}</td>
                </ng-container>

                <ng-container matColumnDef="closingDate">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="closingDate">Closing Date</th>
                    <td mat-cell *matCellDef="let entry">{{entry.closingDate | traceableFormat}}</td>
                </ng-container>

                <ng-container matColumnDef="country">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="country">Country</th>
                    <td mat-cell *matCellDef="let entry">{{entry.country.code | codeFormat: codeTableCountry | async}}</td>
                </ng-container>

                <ng-container matColumnDef="currencyIso">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="currencyIso">Currency</th>
                    <td mat-cell *matCellDef="let entry">{{entry.currencyIso.code | codeFormat: codeTableCurrency | async}}</td>
                </ng-container>

                <ng-container matColumnDef="fundNAV">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="fundNAV">NAV</th>
                    <td mat-cell *matCellDef="let entry">{{entry.fundNAV | traceableFormat}}</td>
                </ng-container>

                <ng-container matColumnDef="finalBidPrice">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="finalBidPrice">Final Bid</th>
                    <td mat-cell *matCellDef="let entry">{{entry.finalBidPrice | traceableFormat}}</td>
                </ng-container>

                <ng-container matColumnDef="discountToNAV">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="discountToNAV">Discount to NAV</th>
                    <td mat-cell *matCellDef="let entry">{{entry.discountToNAV | traceableFormat}}</td>
                </ng-container>

                <ng-container matColumnDef="deals">
                    <th mat-header-cell *matHeaderCellDef>Deals</th>
                    <td mat-cell *matCellDef="let entry" (click)="$event.stopPropagation()">
                        @if (entry.dealCompactList.length > 0) {
                            @for (deal of entry.dealCompactList; track deal) {
                                <div class="deal-link-container">
                                    <a (click)="navigateToFundValuation(entry, deal.id)">{{ deal.projectName.text }}</a>
                                </div>
                            }
                        } @else {
                            -
                        }
                    </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns"
                    (click)="navigateToFundValuation(row)"
                    (contextmenu)="onRowContextMenu($event, row)"></tr>
            </table>
        </div>

        <valumize-context-menu #ctxMenu></valumize-context-menu>

        @if (params) {
            <mat-paginator
                [length]="(totalElements$ | async) ?? 0"
                [pageIndex]="params.page"
                [pageSize]="params.size"
                [pageSizeOptions]="[10, 20, 30, 50, 100]"
                showFirstLastButtons
                (page)="onPageChange($event)">
            </mat-paginator>
        }
    </mat-card-content>
</mat-card>
